import { UseQueryResult } from "react-query";
import { useMemo } from "react";
import { RemoteApiError } from "../useRemote/types";
import { useRemoteQuery } from "../useRemote/useRemoteQuery";
import { VehicleInventory } from "../../../components/FleetManagement/types/VehicleInventory.types";
import { FleetManagementRow } from "../../../components/FleetManagement/types/FleetManagement-row.types";
import { mapVehicleInventoryToFleetManagementRow } from "../../../components/FleetManagement/helpers/mapVehicleInventoryToFleetManagementRow.function";

/** Optional date range for occupiedBooking (YYYY-MM-DD). When both are set, backend returns occupiedBooking per vehicle. */
export type FleetDateRangeParams = {
  from: string;
  to: string;
};

/**
 * Fetches fleet vehicles from backend (GET /fleet/vehicles).
 * When dateRange is provided, sends from/to query params so the backend returns occupiedBooking per vehicle.
 * When excludeBlocked is true, blocked vehicles are excluded (e.g. for the fleet booking process).
 * Maps VehicleInventory[] → FleetManagementRow[]
 */
export function useRemoteFetchFleetData(options?: {
  enabled?: boolean;
  /** When set, from/to are sent as query params so backend populates occupiedBooking */
  dateRange?: FleetDateRangeParams | null;
  /** When true, backend excludes blocked vehicles (for the fleet booking process) */
  excludeBlocked?: boolean;
}): UseQueryResult<FleetManagementRow[], RemoteApiError> {
  const params = useMemo(() => {
    const base =
      options?.dateRange?.from && options?.dateRange?.to
        ? { from: options.dateRange.from, to: options.dateRange.to }
        : {};
    const withExclude =
      options?.excludeBlocked === true ? { ...base, excludeBlocked: "true" as const } : base;
    return Object.keys(withExclude).length > 0 ? withExclude : undefined;
  }, [options?.dateRange?.from, options?.dateRange?.to, options?.excludeBlocked]);

  const queryResult = useRemoteQuery<VehicleInventory[], RemoteApiError>({
    queryKey: [
      "fleet",
      "vehicles",
      params?.from ?? null,
      params?.to ?? null,
      options?.excludeBlocked ?? null
    ],
    url: "/fleet/vehicles",
    params,
    enabled: options?.enabled !== false
  });

  const normalizedData = useMemo(
    () =>
      queryResult.data ? queryResult.data.map(mapVehicleInventoryToFleetManagementRow) : undefined,
    [queryResult.data]
  );

  return { ...queryResult, data: normalizedData } as UseQueryResult<
    FleetManagementRow[],
    RemoteApiError
  >;
}
